/**
 * user.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Rule } from "../permissions/rules";
import { Group, GroupData } from "./group";
import { ZoneData } from "./zone";
import { UserLoginData }
  from "../../server/messages/group-management/group-users-response";
import { ExtendedUserData }
  from "../../server/messages/group-management/user-response";

/**
 * A {@link User} registered within the application.
 */
export class User
{
  constructor (
    /**
     * The user's name (usually first name + last name), as used to present
     * their identity to other humans.
     */
    public name : string,
    /**
     * The unique string used to identify the user to the application during
     * authentication. (In Advocus, this is expected to be an email address.)
     */
    public login : string = "",
    /**
     * A string used to identify a key organizational structure the user belongs
     * to. This is only used as a clue for identifying the user and/or their
     * real-world roles to other humans and has no in-app function.
     */
    public org : string = ""
  ) {}

  /**
   * The security {@link Group}s the user belongs to. The "full" list is
   * filtered through layers of security whitelists and by intersection with the
   * groups known by / accessible to the current user, and so will often be a
   * proper subset of the true list.
   */
  public groups : Group[] = [];

  public groupById (id : number) : Group | undefined {
    return this.groups.find(g => g.id === id);
  }

  /**
   * The {@link Group}s managed by this user in some capacity.
   */
  public managedGroups : Group[] = [];

  /**
   * The {@link Group}s that this user can see shared lists for.
   */
  public sharedListGroups : Group[] = [];

  /**
   * The opaque ID used to identify this user for management actions, such as
   * editing their permissions, details, membership, and work lists.
   */
  public managementId : number;

  public zones : ZoneData[];

  public loadedManagementDetails : boolean = false;

  public isSelf : boolean = false;

  public isActive : boolean;

  public phone : string;

  public languageCodeID : number;
  public countryCodeID : number;
  public languageCode : string;
  public countryCode : string;
  public objectId: number;

  /**
   * Create a new {@link User} from the data returned by a management request.
   * @param data User data made available to a manager
   */
  public static fromManagementRecord (data : UserLoginData) : User
  {
    const u : User = new User(data.name);
    u.login = data.login;
    u.managementId = data.recordId;
    return u;
  }

  public extendWithUserResponse (
    data : ExtendedUserData,
    langCodes : string[],
    countryCodes : string[]
  ) : void
  {
    this.isSelf = data.isCurrent;
    this.isActive = data.isActive;
    this.login = data.email;
    this.phone = data.phone;
    this.languageCodeID = data["language code index"];
    this.countryCodeID = data["country code index"];
    this.languageCode = langCodes[data["language code index"]];
    this.countryCode = countryCodes[data["country code index"]];
    this.readGroups(data.groups);
    this.zones = data.zones;
    this.loadedManagementDetails = true;
    this.objectId = data.objectId;
  }

  protected readGroups (groups : GroupData[]) : void
  {
    this.groups = groups.map((data : GroupData) => new Group(data, this)).sort(
      (a : Group, b : Group) => a.name.localeCompare(b.name));
    this.groups.forEach((g : Group) => {
      if (g.primaryGroupId)
      {
        g.primaryGroup = this.groups.find(
          (pg : Group) => pg.id === g.primaryGroupId);
      }
      if (g.allows(Rule.VIEW_GROUP_USERS))
      {
        if (!this.managedGroups.find((managedGroup: Group) => managedGroup.id === g.id)) {
          this.managedGroups.push(g);
        }
      }
      if (g.allows(Rule.VIEW_SHARED_WORKLIST)) {
        if (!this.sharedListGroups.find((shareGroup: Group) => shareGroup.id === g.id)) {
          this.sharedListGroups.push(g);
        }
      }
    });
  }

  /**
   * Compare two users first by organization and second by name.
   * @param a First user
   * @param b Second user
   */
  public static compare (a : User, b : User) : number
  {
    const orgComparison : number = a.org.localeCompare(b.org);
    return (
      orgComparison !== 0
        ? orgComparison
        : a.name.localeCompare(b.name)
    );
  }

  /**
   * Sort a list of users based upon the default comparison method (above).
   * @param users An array of {@link User}s to be sorted
   */
  public static sort<T extends User> (users : T[]) : T[]
  {
    return users.sort(
        (a : User, b : User) => User.compare(a, b)
      );
  }
}

/**
 * Self is the current active {@link User} of the application and includes
 * additional data that usually isn't known about other users.
 */
// tslint:disable-next-line:max-classes-per-file
export class Self extends User
{
  constructor (
    name : string = "no user data",
    login : string = "",
    org : string = "",
    groups : GroupData[] = [],
    /**
     * List of enumerated permission {@link Rule}s this user has,
     */
    public rules : Rule[] = [],
    /**
     * Is this user a development user? (Should they have access to development
     * tools and features in the client?)
     */
    public dev : boolean = false,
    /**
     * Is this user a support user (who has permissions to manage support 
     * requests and issue reports)?
     */
    public support : boolean = false,
    /**
     * The minimum number of secure prompts this user must have set to create a
     * valid password reset scheme for themselves. (Note that this may be upped
     * by the current zone settings.)
     */
    public minumumSecurePrompts : number = 1,
    /**
     * All zones, including the current, this user could theoretically log into.
     */
    public zones : ZoneData[] = [],

    public guest : boolean = false
  )
  {
    super(name, login, org);
    // Construct group objects from the group data provided
    this.readGroups(groups);
  }

  public readonly isSelf : boolean = true;

  /**
   * Does this user have permission to do aggregation or data export ops?
   * For the purposes of this feature bundle, we only check RUN rules, since
   * users only having CREATE rules would be fairly illogical.
   */
  public isAggregationUser () : boolean
  {
    return this.hasRule(Rule.RUN_AGGREGATOR) ||
      this.hasRule(Rule.RUN_DATA_EXPORT);
  }

  public isManagementUser () : boolean
  {
    return this.hasRule(Rule.VIEW_GROUP_USERS); // TODO: others
  }

  public groupIds () : number[]
  {
    return this.groups.map((g : Group) => g.id);
  }

  public get algorithmDev () : boolean
  {
    return this.rules.indexOf(Rule.DEVELOP_ALGORITHM) > -1;
  }

  private cachedAggRules : Rule[] = null;
  public get aggregateRules () : Rule[]
  {
    // TODO cached version
    return [].concat.apply(this.rules, this.groups.map(
      (g : Group) => g.rules));
  }

  private cachedRuleLookups : Map<Rule, boolean> = new Map();
  public hasRule (r : Rule) : boolean
  {
    if (!this.cachedRuleLookups.has(r))
    {
      this.cachedRuleLookups.set(r, (
        this.rules.indexOf(r) > -1
          || this.aggregateRules.indexOf(r) > -1
      ));
    }
    return this.cachedRuleLookups.get(r);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/identity/user.ts